// This is a mixin that is used to format the order items
// It is used in cart component and confirmation page

import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
const sortAlphaNum = (a, b) => a.localeCompare(b, 'en', { numeric: true })
export default {
  data () {
    return {
      reformatCartOrderItems: false
    }
  },
  computed: {
    cartFormattedOrderItems () {
      const cartOrderItems = this.order.orderItems?.data || []

      if (!cartOrderItems) {
        return {}
      }

      if (this.reformatCartOrderItems) {
        // Do nothing - reformatCartOrderItems is used to instruct Vue that this property needs to be recomputed
      }

      const results = {}
      // Group things they way we will display them in the shopping cart

      // Tickets grouped by showing
      /*
        {
          tickets: [
            showing: { movie, ... }
            memberTickets: [{
              user,
              price
            }],
            guestTicketsByClassAndCount: [{
              userType,
              count,
              price,
              total
            }]
          ]
        }
        */
      results.tickets = _(cartOrderItems)
        .filter({ type: 'ticket' })
        .groupBy('ticket.showing.id')
        .values()
        .sortBy((orderItems) => _(cartOrderItems).map('id').indexOf(orderItems[0].id)) // Sort by original order
        .map((orderItems) => {
          const memberTickets = _.filter(orderItems, (orderItem) => orderItem.ticket.user && orderItem.ticket.user)
          const guestTickets = _(orderItems).filter((orderItem) => (!orderItem.ticket.user || !orderItem.ticket.user)).groupBy('ticketEquivalencyString').values().map((orderItems) => {
            const ticketType = orderItems[0].ticket.ticketType
            const count = orderItems.length

            const displayPrice = _.sum(_.map(orderItems, 'displayPrice'))
            const displayRedemptionTotal = _.sum(_.map(orderItems, 'displayRedemptionTotal'))
            const displayTotal = _.sum(_.map(orderItems, 'displayTotal'))
            const seatNames = _(orderItems).map('ticket.seatName').compact().value().sort(sortAlphaNum).join(', ')

            const showing = orderItems[0].ticket.showing
            const needsSeatSelection = showing.reservedSeating && !_.every(_.map(orderItems, 'ticket.seatName'))

            return {
              orderItems,
              firstOrderItem: orderItems[0],
              lastOrderItem: orderItems[orderItems.length - 1],
              ticketType,
              showing,
              count,
              needsSeatSelection,
              individualPrice: orderItems[0].displayPrice,
              displayPrice,
              displayTotal,
              displayRedemptionTotal,
              seatNames
            }
          }).value()

          const count = orderItems.length
          const displayTotal = _(orderItems).map(orderItem => {
            if (orderItem.includeBookingFeeInPrice) {
              return orderItem.displayTotal
            } else if (this.currentSite.consumerConfig.showBookingFeeInCart) {
              return orderItem.displayTotal + orderItem.bookingFeeDisplayPrice
            } else {
              return orderItem.displayTotal
            }
          }).sum()
          const showing = orderItems[0].ticket.showing

          return {
            showing,
            memberTickets,
            guestTickets,
            orderItems,
            count,
            displayTotal
          }
        }).value()

      results.bookingFees = _(cartOrderItems)
        .filter(orderItem => orderItem.type === 'booking-fee' || (_.includes(ORDER_ITEM_TYPES.BOOKING_FEE_CONDITION, orderItem.type) && !!orderItem.localBookingFeeType))
        .groupBy(orderItem => orderItem.localBookingFeeType?.name || orderItem.description)
        .values()
        .sortBy((orderItems) => (_.first(orderItems).bookingFeeType || _.first(orderItems).localBookingFeeType)?.priorityOrder)
        .map((orderItems) => {
          const bookingFeeType = _.first(orderItems).localBookingFeeType || _.first(orderItems).bookingFeeType
          const bookingFeeName = bookingFeeType?.name || _.first(orderItems).description
          const displayPrice = _(orderItems).map(orderItem => {
            let bookingFeePrice = 0

            if (_.includes(ORDER_ITEM_TYPES.BOOKING_FEE_CONDITION, orderItem.type) && (orderItem.setAsGross && !this.displayTotals)) {
              bookingFeePrice = orderItem.bookingFeeTotal
            } else if (_.includes(ORDER_ITEM_TYPES.BOOKING_FEE_CONDITION, orderItem.type)) {
              bookingFeePrice = orderItem.bookingFeeAmount
            } else if (orderItem.type === 'booking-fee' && !this.displayTotals) {
              bookingFeePrice = orderItem.displayPrice
            } else if (orderItem.type === 'booking-fee') {
              bookingFeePrice = orderItem.subtotal
            }

            return bookingFeePrice
          }).sum()
          const count = orderItems.length

          return {
            id: bookingFeeType?.id,
            name: bookingFeeName,
            orderItems,
            count,
            displayPrice
          }
        }).value()

      results.activityTickets = _(cartOrderItems)
        .filter({ type: 'activity-ticket' })
        .groupBy('activityTicket.activityBookingId')
        .values()
        .sortBy((orderItems) => _(cartOrderItems).map('id').indexOf(orderItems[0].id)) // Sort by original order
        .map((orderItems) => {
          const memberTickets = _.filter(orderItems, (orderItem) => !!orderItem.activityTicket.user)
          const guestTickets = _(orderItems).filter((orderItem) => !orderItem.activityTicket.user).groupBy('activityTicketEquivalencyString').values().map((orderItems) => {
            const ticketType = orderItems[0].activityTicket.ticketType
            const count = orderItems.length

            const displayPrice = _.sum(_.map(orderItems, 'displayPrice'))
            const displayRedemptionTotal = _.sum(_.map(orderItems, 'displayRedemptionTotal'))
            const displayTotal = _.sum(_.map(orderItems, 'displayTotal'))

            return {
              orderItems,
              firstOrderItem: orderItems[0],
              lastOrderItem: orderItems[orderItems.length - 1],
              ticketType,
              count,
              individualPrice: orderItems[0].displayPrice,
              displayPrice,
              displayTotal,
              displayRedemptionTotal
            }
          }).value()

          const count = orderItems.length
          const displayTotal = _.sum(_.map(orderItems, 'displayTotal'))
          const activityBooking = _.find(this.order.activityBookings.data, { id: orderItems[0].activityTicket.activityBookingId })
          const activitySession = activityBooking?.activitySession

          return {
            activityBooking,
            activitySession,
            memberTickets,
            guestTickets,
            orderItems,
            count,
            displayTotal
          }
        }).value()

      function createSalesItemsFilterFunction (belongsToActivityBooking = false) {
        return function (orderItem) {
          return orderItem.type === 'item' && (belongsToActivityBooking ? !_.isNil(orderItem.activityBookingId) : _.isNil(orderItem.activityBookingId))
        }
      }

      // Concession Items grouped by exact match. Supports expansion.
      results.items = _(cartOrderItems).filter(createSalesItemsFilterFunction()).groupBy('itemEquivalencyString').values().map((orderItems) => {
        const count = orderItems.length
        const displayPrice = _.sum(_.map(orderItems, 'displayPrice'))
        const displayRedemptionTotal = _.sum(_.map(orderItems, 'displayRedemptionTotal'))
        const displayTotal = _.sum(_.map(orderItems, 'displayTotal'))

        return {
          firstOrderItem: orderItems[0],
          lastOrderItem: orderItems[orderItems.length - 1],
          orderItems,
          count,
          displayPrice,
          displayRedemptionTotal,
          displayTotal
        }
      }).value()

      results.activityItems = _(cartOrderItems).filter(createSalesItemsFilterFunction(true)).groupBy('itemEquivalencyString').values().map((orderItems) => {
        const count = orderItems.length
        const displayPrice = _.sum(_.map(orderItems, 'displayPrice'))
        const displayRedemptionTotal = _.sum(_.map(orderItems, 'displayRedemptionTotal'))
        const displayTotal = _.sum(_.map(orderItems, 'displayTotal'))

        return {
          firstOrderItem: orderItems[0],
          lastOrderItem: orderItems[orderItems.length - 1],
          orderItems,
          count,
          displayPrice,
          displayRedemptionTotal,
          displayTotal
        }
      }).value()

      // Membership intervals
      results.membershipIntervals = _(cartOrderItems).filter({ type: 'membership-interval' }).value()

      results.dayPass = _.find(cartOrderItems, { type: 'day-pass' })

      // Vouchers
      results.vouchers = _(cartOrderItems)
        .filter(orderItem => orderItem.group && (orderItem.type === 'voucher-purchase' || orderItem.type === 'voucher-service-issue'))
        .groupBy('voucherEquivalencyString')
        .values()
        .concat(
          _(cartOrderItems)
            .filter(orderItem => !orderItem.group && (orderItem.type === 'voucher-purchase' || orderItem.type === 'voucher-service-issue'))
            .map(orderItem => [orderItem]).value())
        .sortBy((orderItems) => _(cartOrderItems).map('id').indexOf(orderItems[0].id)) // Sort by original order
        .map((orderItems) => {
          const voucherType = orderItems[0].voucherPurchase.voucherType
          const count = orderItems.length

          const displayPrice = _.sum(_.map(orderItems, 'displayPrice'))
          const displayRedemptionTotal = _.sum(_.map(orderItems, 'displayRedemptionTotal'))
          const displayTotal = _.sum(_.map(orderItems, 'displayTotal'))

          return {
            orderItems,
            firstOrderItem: orderItems[0],
            lastOrderItem: orderItems[orderItems.length - 1],
            voucherType,
            count,
            individualPrice: orderItems[0].displayPrice,
            displayPrice,
            displayTotal,
            displayRedemptionTotal
          }
        }).value()

      // Gift Cards
      results.giftCards = _(cartOrderItems)
        .filter(orderItem => orderItem.group && orderItem.type === 'gift-card')
        .groupBy('giftCardEquivalencyString')
        .values()
        .concat(
          _(cartOrderItems)
            .filter(orderItem => !orderItem.group && orderItem.type === 'gift-card')
            .map(orderItem => [orderItem]).value())
        .sortBy((orderItems) => _(cartOrderItems).map('id').indexOf(orderItems[0].id)) // Sort by original order
        .map((orderItems) => {
          const count = orderItems.length
          const displayPrice = _.sum(_.map(orderItems, 'displayPrice'))
          const displayRedemptionTotal = _.sum(_.map(orderItems, 'displayRedemptionTotal'))
          const displayTotal = _.sum(_.map(orderItems, 'displayTotal'))

          return {
            orderItems,
            firstOrderItem: orderItems[0],
            lastOrderItem: orderItems[orderItems.length - 1],
            count,
            individualPrice: orderItems[0].displayPrice,
            displayPrice,
            displayTotal,
            displayRedemptionTotal
          }
        }).value()

      results.voucherPackages = _.filter(cartOrderItems, orderItem => orderItem.type === 'voucher-package-purchase')

      // Misc Items
      results.miscItems = _.filter(cartOrderItems, orderItem => _.includes(['misc-item', 'user-credit', 'donation', 'tip'], orderItem.type))

      return results
    }
  }
}
